import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessKey, businessesKey } from "@/business/query";
import { useApi } from "@/core/api";
import {
  isOnboardingConflict,
  onboardingKey,
  onboardingPath,
} from "./onboarding-api";
import { getUploadProgress, onboardingDocumentsKey } from "./query";
import type {
  BusinessOnboarding,
  IntakeStep,
  OnboardingAnswerUpdates,
  OnboardingNavigation,
} from "./types";

// The payload changes on its own only on the steps that wait for email: the forwarding
// probe coming back through the customer's rule, the provider's verification code
// landing in our mailbox, and forwarded invoices arriving in the list. The customer is
// watching the screen for those, so they are worth a few seconds.
const LIVE_MAIL_STEPS: readonly IntakeStep[] = [
  "forwarding_setup",
  "invoice_upload",
];
const LIVE_INTERVAL_MS = 5_000;
// Everywhere else the only thing that moves without the customer is staff work in
// Admin, which nobody sits waiting for. This query also invalidates the business cache
// when setup_state changes, so it is what carries "Volca finished your setup" too.
const IDLE_INTERVAL_MS = 30_000;

export const useBusinessOnboarding = (
  businessId?: string | null,
  enabled = true,
  liveMail = false,
) => {
  const api = useApi();
  const client = useQueryClient();
  return useQuery({
    queryKey: onboardingKey(businessId),
    queryFn: async ({ signal }): Promise<BusinessOnboarding> => {
      const { data } = await api.get<BusinessOnboarding>(
        onboardingPath(businessId!),
        { signal },
      );
      const previous = client.getQueryData<BusinessOnboarding>(
        onboardingKey(businessId),
      );
      // Staff may change products, reopen documents, or complete setup while
      // the customer has this tab open. Refresh sibling caches when state changes.
      if (
        !previous ||
        previous.setup_state !== data.setup_state ||
        previous.submitted_at !== data.submitted_at ||
        JSON.stringify(previous.steps) !== JSON.stringify(data.steps) ||
        JSON.stringify(previous.step_progress) !==
          JSON.stringify(data.step_progress)
      ) {
        void client.invalidateQueries({ queryKey: businessKey(businessId) });
        void client.invalidateQueries({ queryKey: businessesKey });
        void client.invalidateQueries({
          queryKey: onboardingDocumentsKey(businessId!),
        });
      }
      return data;
    },
    enabled: Boolean(businessId) && enabled,
    refetchInterval: ({ state }) =>
      state.data &&
      (liveMail || LIVE_MAIL_STEPS.includes(state.data.current_step))
        ? LIVE_INTERVAL_MS
        : IDLE_INTERVAL_MS,
    refetchOnWindowFocus: "always",
    retry: false,
  });
};

/**
 * Every onboarding write answers with the whole record, so two in flight at once race:
 * a slow upload started before a checklist jump answers after it, with the position as
 * it was, and the screen goes back a step.
 *
 * Scoping them to the business makes React Query run them one at a time, so the
 * responses arrive in the order the server handled them and the last one applied is the
 * last one that happened. Only one upload is ever in flight, so a queued jump waits for
 * a single file rather than a whole batch.
 */
const onboardingWriteScope = (businessId: string) => ({
  id: `onboarding-write-${businessId}`,
});

const useOnboardingCache = (businessId: string) => {
  const client = useQueryClient();
  return {
    scope: onboardingWriteScope(businessId),
    onSuccess: async (state: BusinessOnboarding) => {
      await client.cancelQueries({ queryKey: onboardingKey(businessId) });
      client.setQueryData(onboardingKey(businessId), state);
      // The setup and product selectors use both caches.
      void client.invalidateQueries({ queryKey: businessKey(businessId) });
      void client.invalidateQueries({ queryKey: businessesKey });
    },
    onError: async (error: unknown) => {
      if (isOnboardingConflict(error)) {
        await client.invalidateQueries({ queryKey: onboardingKey(businessId) });
        await client.invalidateQueries({ queryKey: businessKey(businessId) });
      }
    },
  };
};

export const useOnboardingNavigation = (businessId: string) => {
  const api = useApi();
  const cache = useOnboardingCache(businessId);
  return useMutation({
    mutationFn: ({
      business,
      ...onboarding
    }: OnboardingNavigation): Promise<BusinessOnboarding> =>
      api
        .post(`${onboardingPath(businessId)}/navigation`, {
          onboarding,
          ...(business ? { business } : {}),
        })
        .then(({ data }) => data),
    ...cache,
  });
};

export const useSaveOnboardingAnswers = (businessId: string) => {
  const api = useApi();
  const cache = useOnboardingCache(businessId);
  return useMutation({
    mutationFn: (
      answers: OnboardingAnswerUpdates,
    ): Promise<BusinessOnboarding> =>
      api
        .patch(onboardingPath(businessId), { onboarding: { answers } })
        .then(({ data }) => data),
    ...cache,
  });
};

export const useReopenOnboarding = (businessId: string) => {
  const api = useApi();
  const cache = useOnboardingCache(businessId);
  return useMutation({
    mutationFn: (step: IntakeStep): Promise<BusinessOnboarding> =>
      api
        .delete(`${onboardingPath(businessId)}/submission`, {
          data: { onboarding: { step } },
        })
        .then(({ data }) => data),
    ...cache,
  });
};

export const useStartForwardingCheck = (businessId: string) => {
  const api = useApi();
  const cache = useOnboardingCache(businessId);
  return useMutation({
    mutationFn: (): Promise<BusinessOnboarding> =>
      api
        .post(`${onboardingPath(businessId)}/forwarding_check`)
        .then(({ data }) => data),
    ...cache,
  });
};

export const useUploadInvoiceSample = (businessId: string) => {
  const api = useApi();
  const cache = useOnboardingCache(businessId);
  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress: (progress: number) => void;
    }): Promise<BusinessOnboarding> => {
      const body = new FormData();
      body.append("file", file, file.name);
      return api
        .post(`${onboardingPath(businessId)}/invoice_samples`, body, {
          onUploadProgress: (event) => onProgress(getUploadProgress(event)),
        })
        .then(({ data }) => data);
    },
    ...cache,
  });
};
